import {
  initializeAnalytics,
  isSupported,
  logEvent,
  setConsent,
  setDefaultEventParameters,
  type Analytics,
} from "firebase/analytics";
import { getFirebaseServices } from "./firebase";

type TelemetryParameters = Record<string, string | number | boolean>;

const FIRST_OPENED_KEY = "logbookanaes.studio.analytics.first_opened_at";
const ONCE_KEY_PREFIX = "logbookanaes.studio.analytics.once.";
const onceInFlight = new Set<string>();

let analyticsPromise: Promise<Analytics | null> | undefined;

function analyticsAllowed(): boolean {
  if (import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true") return false;
  try {
    const navigatorWithPrivacy = navigator as Navigator & { globalPrivacyControl?: boolean };
    const windowWithPrivacy = window as Window & { doNotTrack?: string };
    return navigatorWithPrivacy.globalPrivacyControl !== true
      && navigator.doNotTrack !== "1"
      && windowWithPrivacy.doNotTrack !== "1";
  } catch {
    return false;
  }
}

async function getAnalyticsInstance(): Promise<Analytics | null> {
  if (!analyticsAllowed()) return null;
  analyticsPromise ??= (async () => {
    if (!await isSupported().catch(() => false)) return null;
    setConsent({
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    setDefaultEventParameters({
      app_platform: "web",
      app_surface: "studio",
    });
    return initializeAnalytics(getFirebaseServices().app, {
      config: { send_page_view: false },
    });
  })().catch(() => null);
  return analyticsPromise;
}

async function track(
  event: string,
  parameters: TelemetryParameters = {},
): Promise<boolean> {
  try {
    const analytics = await getAnalyticsInstance();
    if (!analytics) return false;
    logEvent(analytics, event, parameters);
    return true;
  } catch {
    return false;
  }
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

async function trackOnce(
  event: string,
  parameters: TelemetryParameters = {},
): Promise<boolean> {
  const key = `${ONCE_KEY_PREFIX}${event}`;
  if (readStorage(key) === "1" || onceInFlight.has(key)) return false;
  onceInFlight.add(key);
  try {
    const tracked = await track(event, parameters);
    if (tracked) writeStorage(key, "1");
    return tracked;
  } finally {
    onceInFlight.delete(key);
  }
}

function utcDay(value: Date): number {
  return Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

export function retentionDay(firstOpened: Date, now: Date): number {
  return Math.floor((utcDay(now) - utcDay(firstOpened)) / 86_400_000);
}

async function recordRetention(now = new Date()): Promise<boolean> {
  const firstOpenedRaw = readStorage(FIRST_OPENED_KEY);
  if (!firstOpenedRaw) {
    writeStorage(FIRST_OPENED_KEY, now.toISOString());
    return false;
  }
  const firstOpened = new Date(firstOpenedRaw);
  if (Number.isNaN(firstOpened.getTime())) {
    writeStorage(FIRST_OPENED_KEY, now.toISOString());
    return false;
  }

  const event = new Map<number, string>([
    [1, "day_1_retention"],
    [7, "day_7_retention"],
    [30, "day_30_retention"],
  ]).get(retentionDay(firstOpened, now));
  return event ? trackOnce(event) : false;
}

export async function recordAppOpened(): Promise<void> {
  await Promise.all([track("app_opened"), recordRetention()]);
}

export async function recordCaseAddition(
  previousCaseCount: number,
  addedCaseCount: number,
): Promise<void> {
  if (!Number.isInteger(previousCaseCount)
    || !Number.isInteger(addedCaseCount)
    || addedCaseCount < 1) return;

  const nextCaseCount = previousCaseCount + addedCaseCount;
  const milestones: readonly (readonly [number, string])[] = [
    [1, "first_case_added"],
    [10, "tenth_case_added"],
    [50, "fiftieth_case_added"],
    [100, "hundredth_case_added"],
  ];
  await Promise.all(milestones
    .filter(([count]) => previousCaseCount < count && nextCaseCount >= count)
    .map(([count, event]) => trackOnce(event, { case_count: count })));
}

export async function recordFirstReportGenerated(reportType: string): Promise<void> {
  await trackOnce("first_report_generated", {
    report_type: reportType.slice(0, 100),
  });
}

export async function recordFirstSyncCompleted(): Promise<void> {
  await trackOnce("first_sync_completed");
}

export async function recordFirstProPurchase(): Promise<void> {
  await trackOnce("first_pro_purchase");
}
